import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Typography, Stack, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'

import EmptyScreen from 'packages/ui/EmptyScreen'
import ScrollTable from 'components/global/CUK/ScrollTable'
import TableView, { useTable, IItem } from 'packages/ui/Table'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'

import { useSort } from '../store'
import useList from '../use-event-ssids'
import COLUMNS_CONFIG from './columns-config'
import { trackEvent } from 'analytics'

const Table = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const apiList = useList()
  const [sort, setSort] = useSort()
  const { t } = useTranslation()
  
  const context = useMemo(() => ({
    t,
    onEdit: (itemId) => {
      trackEvent('wifi_management_edit')
      navigate(`/event-ssids/${itemId}/edit`)
    }
  }), [navigate, t])

  const columns = useMemo(() => 
    COLUMNS_CONFIG.map(col => ({
      ...col,
      value: typeof col.value === 'function' 
        ? (item) => col.value(item, context)
        : col.value
    })), 
    [context]
  )
  
  const apiTable = useTable({
    rowKey: 'id',
    loading: apiList.loading,
    loadingNext: apiList.loadingNext,
    items: apiList.items as unknown as IItem[],
    columns,
    onRowClick: (item) => {
      trackEvent('wifi_management_details')
      navigate(`/event-ssids/${item.id}`)
    }
  })

  if (!apiList.loading && !apiList.items.length) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <EmptyScreen icon="calendar-star" iconSize="80px">
          {t('event_ssids.no_data')}
        </EmptyScreen>
      </Box>
    )
  }

  return (
    <ScrollTable
      fetchNextPage={apiList.fetchNextPage}
      shouldFetchNextPage={apiList.shouldFetchNextPage}
      collapse={Boolean(id)}
      style={{ 
        height: '100%', 
        overflow: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}
    >
      {
        apiList.loading
          ? <TableSkeleton />
          : apiList.error
            ? <Stack justifyContent={'center'} alignItems={'center'} sx={{ height: '100%' }}>
                <Typography component={'span'} sx={{ fontSize: 12, paddingLeft: 1 }} color={'error'}>
                  {t(`errors.${apiList.error}`)}
                </Typography>
              </Stack>
            : <TableView
                apiTable={apiTable}
                sort={{
                  ...sort,
                  direction: sort.direction as string,
                  by: sort.by as string,
                  onChange: name => {
                    const column = COLUMNS_CONFIG.find(column => column.name === name)
                    const sortBy = column?.sortBy || column?.name
                    if (sortBy) {
                      setSort(sortBy)
                    }
                  }
                }}
              />
      }
    </ScrollTable>
  )
}

export default Table